/**
 * MeleeCone — facing-relative cone hit-testing, shared by client prediction and
 * the server-authoritative area scan.
 *
 * `COMBAT-MELEE-CONE-01` (2026-07-26, DJ): melee must not require a selected
 * target and must sweep a forward arc rather than resolving one nearest enemy.
 *
 * Why this lives in `shared/logic` and is a PURE function: the client uses it to
 * predict which enemies a swing hits, and the server uses the identical maths to
 * decide what actually takes damage. Two implementations would desync — the
 * client would flash damage numbers on enemies the server never hit. See
 * `AttackRange.ts`, which exists for exactly the same reason after the melee /
 * ranged classification drifted between client and server twice.
 *
 * Geometry note — this is a 2D side-on game, so a "cone" is a wedge in the XY
 * plane opening along the facing axis (+X when facing right, -X when facing
 * left). `arcDeg` is the FULL opening angle, so 120 means +/-60 from the facing
 * axis. 360 degenerates to a plain radius, which is a legitimate config (and is
 * what every existing `shape: 'cone'` ability effectively got server-side before
 * this landed, since the server only ever ran a radius scan).
 */

import { nearestBodyPoint, reachToBody, segmentTouchesBody, type ReachBody } from './BodyReach';

export type Facing = 'left' | 'right';

export interface ConeSpec {
  /** Reach in pixels, measured from the attacker's origin. */
  rangePx: number;
  /** FULL opening angle in degrees. 120 = 60 either side of facing. */
  arcDeg: number;
  /**
   * Plan 29 D55 — an ASYMMETRIC vertical spread, in degrees above (`upDeg`)
   * and below (`downDeg`) the facing axis. Both present = the wedge runs from
   * `upDeg` above facing to `downDeg` below it and `arcDeg` is ignored for the
   * bearing test; absent = symmetric `arcDeg / 2` either side (the original
   * cone, byte-for-byte). `resolveMeleeCone` fills these for melee swings from
   * `MELEE_SWING_GEOMETRY`, so the server scan, the client prediction and the
   * drawn wash read the same two numbers.
   */
  upDeg?: number;
  downDeg?: number;
}

/** The cone's half-angles above and below facing (degrees), clamped to 0..180. */
export function coneHalfAngles(spec: ConeSpec): { up: number; down: number } {
  const half = Math.max(0, Math.min(360, spec.arcDeg)) / 2;
  const clamp = (v: number) => Math.max(0, Math.min(180, v));
  if (typeof spec.upDeg === 'number' && typeof spec.downDeg === 'number'
    && Number.isFinite(spec.upDeg) && Number.isFinite(spec.downDeg)) {
    return { up: clamp(spec.upDeg), down: clamp(spec.downDeg) };
  }
  return { up: clamp(half), down: clamp(half) };
}

/** True when the cone is a full circle (no bearing test at all). */
function isFullCircle(spec: ConeSpec): boolean {
  const { up, down } = coneHalfAngles(spec);
  return up + down >= 360 - 1e-9;
}

export interface ConePoint {
  x: number;
  y: number;
}

/**
 * True when the cone centred on `facing` at `origin` touches `target`.
 *
 * `target` may carry a body size (`width`/`height`, centred on `x`/`y`): then
 * reach and bearing are measured to the BODY, not its centre (plan 07/08
 * reach audit — see BodyReach.ts). The test is exact for a box: the wedge
 * touches it when the box's nearest point is in range and inside the arc, or
 * when either edge ray of the wedge (length `rangePx`) crosses the box. A
 * target with no size is a point and keeps the original centre test.
 *
 * A target at (or extremely near) the origin — or a body the origin stands
 * inside — always counts as hit: there is no meaningful bearing, and
 * excluding it would make point-blank swings whiff, which is the exact feel
 * complaint this change exists to fix.
 */
export function isInCone(
  origin: ConePoint,
  facing: Facing,
  target: ReachBody,
  spec: ConeSpec,
): boolean {
  const near = nearestBodyPoint(origin, target);
  const dx = near.x - origin.x;
  const dy = near.y - origin.y;
  const distSq = dx * dx + dy * dy;

  const range = Math.max(0, spec.rangePx);
  if (distSq > range * range) return false;

  // Point-blank: no meaningful bearing, always in.
  if (distSq < 1) return true;

  // A full circle short-circuits the trigonometry (and avoids float edge cases
  // at exactly 180 degrees of half-arc).
  if (isFullCircle(spec)) return true;
  const { up, down } = coneHalfAngles(spec);

  const forwardX = facing === 'right' ? 1 : -1;
  // Signed bearing from the facing axis: negative = above (screen Y grows
  // down), positive = below. With a symmetric spec this is the same test as
  // the original |theta| <= arc / 2.
  const thetaDeg = (Math.atan2(dy, dx * forwardX) * 180) / Math.PI;
  if (thetaDeg >= -up && thetaDeg <= down) return true;

  // The nearest point is outside the arc. A box is convex and distance along
  // a segment peaks at its ends, so any part of the box inside the wedge is
  // joined to the nearest point by a path that crosses an edge ray within
  // range — testing both edge rays is therefore exact, not an approximation.
  if (!(target.width && target.width > 0) && !(target.height && target.height > 0)) return false;
  for (const theta of coneEdgeAngles(facing, spec)) {
    const end = { x: origin.x + range * Math.cos(theta), y: origin.y + range * Math.sin(theta) };
    if (segmentTouchesBody(origin, end, target)) return true;
  }
  return false;
}

/**
 * World-space angles (radians, Y down) of the wedge's two edge rays: the
 * upper edge and the lower edge, mirrored across the vertical for a
 * left-facing attacker.
 */
export function coneEdgeAngles(facing: Facing, spec: ConeSpec): [number, number] {
  const { up, down } = coneHalfAngles(spec);
  const a = (-up * Math.PI) / 180;
  const b = (down * Math.PI) / 180;
  return facing === 'right' ? [a, b] : [Math.PI - a, Math.PI - b];
}

/** A swing candidate: centre point plus (optionally) its body size. */
export interface ConeCandidate extends ReachBody {
  id: string;
}

/** In-cone candidates split by the `maxTargets` cap. See `planConeSweep`. */
export interface ConeSweepPlan<T extends ConeCandidate> {
  /** Nearest-first, capped at `maxTargets` — the targets that take damage. */
  hit: T[];
  /**
   * Inside the arc but past the cap, nearest first. Empty when uncapped.
   *
   * Exists so a debug overlay can show WHY an enemy in the wedge took nothing
   * (`MeleeConeOverlayRenderer`) instead of looking like a missed hit. Reading
   * it never changes what `hit` contains.
   */
  capped: T[];
}

/**
 * The full verdict for one swing: who is in the arc, and where the
 * `maxTargets` cap fell.
 *
 * Nearest-first ordering matters for the cap: when more enemies are in the arc
 * than a swing may hit, hitting the closest ones is what a player expects.
 *
 * Cap semantics are the SERVER's (`CombatDamageHandler`): the cap is applied to
 * the raw in-cone set, BEFORE any `exclude_ids` — so a primary that already
 * took damage through the single-target path still consumes one of its slots.
 * Nothing here knows about exclusions, which is what keeps the two in step.
 */
export function planConeSweep<T extends ConeCandidate>(
  origin: ConePoint,
  facing: Facing,
  candidates: readonly T[],
  spec: ConeSpec & { maxTargets?: number },
): ConeSweepPlan<T> {
  const hits: Array<{ target: T; dist: number }> = [];
  for (const candidate of candidates) {
    if (!isInCone(origin, facing, candidate, spec)) continue;
    // Nearest BODY first (equal to centre distance for point candidates).
    hits.push({ target: candidate, dist: reachToBody(origin, candidate) });
  }
  hits.sort((a, b) => a.dist - b.dist);
  const cap = spec.maxTargets && spec.maxTargets > 0 ? spec.maxTargets : hits.length;
  return {
    hit: hits.slice(0, cap).map((h) => h.target),
    capped: hits.slice(cap).map((h) => h.target),
  };
}

/**
 * Every candidate inside the cone, nearest first, capped at `maxTargets`
 * (0 or undefined = uncapped).
 */
export function resolveConeTargets<T extends ConeCandidate>(
  origin: ConePoint,
  facing: Facing,
  candidates: readonly T[],
  spec: ConeSpec & { maxTargets?: number },
): T[] {
  return planConeSweep(origin, facing, candidates, spec).hit;
}

/**
 * The wedge as a closed polygon in world space — apex at `origin`, arc swept
 * at `rangePx`, ready to hand straight to `IGraphics.fillPolygon`.
 *
 * WHY THIS LIVES HERE, NEXT TO `isInCone`, AND NOT IN THE RENDERER:
 * on 2026-07-10 the race hazards shipped a MIRRORED hit shape — `hammerArmPose`
 * built its visual arm with `+sin` while Pixi's `setAngle` is clockwise, so the
 * server hit the opposite side from the drawn arm and it cost a playtest plus a
 * root-cause hunt to find. A drawing that derives its own geometry will
 * eventually disagree with the damage. So the polygon is generated from the
 * SAME `ConeSpec` + `Facing` the hit test consumes, in the same module, and
 * `MeleeCone.test.ts` asserts every vertex it emits satisfies `isInCone`.
 *
 * The polygon is INSCRIBED (arc chords fall inside the true arc), so it can
 * only ever under-draw the real hit region, never over-promise reach.
 *
 * `arcDeg >= 360` degenerates to a full circle with no apex vertex — the same
 * legitimate config `isInCone` short-circuits.
 */
export function coneOutlinePoints(
  origin: ConePoint,
  facing: Facing,
  spec: ConeSpec,
  segments = 24,
): ConePoint[] {
  // Strictly inscribed: `isInCone` uses inclusive `<=` comparisons, so a vertex
  // placed at EXACTLY rangePx / exactly the arc edge lands on the boundary and
  // float error in the reconstructed distance/bearing can read it as outside.
  // Shrinking by a relative hair makes "every drawn vertex is a hit" an exact,
  // testable invariant instead of a float coin-flip. At 190px this moves an
  // edge by ~2e-7 px — invisible, and it errs toward under-drawing, which is
  // the safe direction for a hitbox visualization.
  const INSET = 1 - 1e-9;
  const range = Math.max(0, spec.rangePx) * INSET;
  const full = isFullCircle(spec);
  const { up, down } = coneHalfAngles(spec);
  // Even segment count guarantees a vertex exactly on the facing axis for a
  // symmetric cone, i.e. the polygon visibly reaches full `rangePx` ahead.
  const steps = Math.max(2, Math.round(segments / 2) * 2);

  // Facing is the +X / -X axis (2D side-on game), matching isInCone's
  // `forwardX`; negative angles are above facing (screen Y grows down), the
  // same sign the hit test reads. Left mirrors across the vertical.
  const toWorld = (deg: number) => {
    const a = (deg * Math.PI) / 180;
    return facing === 'right' ? a : Math.PI - a;
  };

  const points: ConePoint[] = [];
  if (!full) points.push({ x: origin.x, y: origin.y });
  const startDeg = full ? 0 : -up * INSET;
  const spanDeg = full ? 360 : (up + down) * INSET;
  const last = full ? steps - 1 : steps;
  for (let i = 0; i <= last; i++) {
    const theta = toWorld(startDeg + (spanDeg * i) / steps);
    points.push({ x: origin.x + range * Math.cos(theta), y: origin.y + range * Math.sin(theta) });
  }
  return points;
}
